/**
 * The school's subscription as the app sees it (functions/src/billing.ts does the charging and checks).
 * school_billing/{schoolId} is read live, so a payment or an extension opens the portal straight away.
 * A school with no billing record (KCA, and schools added before billing started) is never paused.
 */
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { DEFAULT_PRICING, type Caps, type CustomTerms, type Kind, type PlanId, type PricingSettings, type Quote, type Term } from './pricingCore';

export const GRACE_DAYS = 14;
export const MEF_CONTACT = { email: 'info@muyikaratu.com' };
const DAY = 864e5;

export interface Billing {
    plan: PlanId; paidUntil: number; exempt?: boolean; parentLevy?: boolean; students?: number;
    referralCode?: string; referredBy?: string; credit?: number; tahfizPack?: number; lastInvoiceId?: string;
    bundles?: Record<string, Partial<Record<Kind, number>>>;
    custom?: CustomTerms | null;
    twoYear?: boolean; founding?: boolean; communityPct?: number;
}
export type SubState = 'exempt' | 'active' | 'grace' | 'paused';

export function stateOf(b: Billing | null | undefined, now = Date.now()): SubState {
    if (!b || b.exempt || !b.paidUntil) return 'exempt';
    if (now <= b.paidUntil) return 'active';
    return now <= b.paidUntil + GRACE_DAYS * DAY ? 'grace' : 'paused';
}
export const daysLeft = (t: number) => Math.ceil((t - Date.now()) / DAY);
export const naira = (n: number) => `${n < 0 ? '−' : ''}₦${Math.round(Math.abs(n)).toLocaleString('en-NG')}`;
export const fmtDate = (t: number) => new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

export function useSubscription(schoolId?: string | null) {
    const [billing, setBilling] = useState<Billing | null | undefined>(undefined);
    useEffect(() => {
        if (!schoolId) { setBilling(null); return; }
        return onSnapshot(doc(db, 'school_billing', schoolId), s => setBilling((s.data() as Billing) || null), () => setBilling(null));
    }, [schoolId]);
    return { billing, loading: billing === undefined, state: stateOf(billing) };
}

export function usePricing() {
    const [p, setP] = useState<PricingSettings>(DEFAULT_PRICING);
    useEffect(() => onSnapshot(doc(db, 'platform_settings', 'pricing'), s => setP({ ...DEFAULT_PRICING, ...(s.data() || {}) } as PricingSettings), () => {}), []);
    return p;
}

const fn = <I, O>(name: string) => (data: I) => httpsCallable<I, O>(getFunctions(), name, { timeout: 60_000 })(data).then(r => r.data);
export type Gateway = 'paystack' | 'flutterwave' | 'stripe' | 'transfer';
export interface BillingStateResult {
    billing: Billing | null; state: SubState; pricing: PricingSettings; counted: number; period: Term;
    usage: Caps; limits: Caps | null;
}
export interface PayResult { invoiceId: string; number: string; paymentUrl?: string | null; bank?: PricingSettings['bank'] | null; total?: number }
export const api = {
    state: fn<{ schoolId?: string }, BillingStateResult>('billingState'),
    quote: fn<{ schoolId?: string; input: any }, { quote: Quote; counted: number }>('billingQuote'),
    checkout: fn<{ schoolId?: string; input: any; gateway: Gateway }, PayResult>('billingCheckout'),
    payInvoice: fn<{ invoiceId: string; gateway: Gateway }, PayResult>('billingPayInvoice'),
    setLevy: fn<{ parentLevy: boolean }, { ok: boolean }>('billingSetLevy'),
    levyReport: fn<{ schoolId?: string; invoiceUnpaid?: boolean }, any>('levyReport'),
    levyStatus: fn<{ studentId?: string }, any>('levyStatus'),
    levyCheckout: fn<{ studentId?: string; email?: string }, { paymentUrl: string }>('levyCheckout'),
    setRate: fn<any, { decision: { move: number; apply: boolean } | null; applied: boolean; pricing: PricingSettings }>('pricingSetRate'),
    markPaid: fn<{ invoiceId: string; ref?: string }, { ok: boolean }>('billingMarkPaid'),
    admin: fn<any, { ok: boolean }>('billingAdmin'),
};

/** one line for the website and forms: the discounts and their ceiling */
export function discountSummary(p: PricingSettings) {
    const vol = (p.volume || [[2000, 20], [1000, 15], [500, 10]]).slice().sort((a, b) => a[0] - b[0]);
    return `Up to ${p.maxDiscountPct ?? 50}% off: whole year ${p.yearlyPct}%, early payment ${p.earlyPct}%, two-year agreement ${p.twoYearPct ?? 10}%, `
        + `${vol.map(([n, v]) => `${n.toLocaleString()}+ students ${v}%`).join(', ')}, founding partner ${p.foundingPct ?? 15}%, community schools up to 20%.`;
}
